<template>
  <div class="content-panel">
    <div class="panel-title-wrap">
      <h3 class="panel-title">{{ $t("tradePwd.title") }}</h3>
      <span class="panel-sub">{{ $t("tradePwd.subtitle") }}</span>
    </div>
    <!-- ===== 未开启两步验证警告 ===== -->
    <div class="twofa-warning" v-if="isTwoFactor != 2">
      <span class="warning-icon">⚠️</span>
      <span class="warning-text">
        {{ $t("tradePwd.warning") }}
      </span>
      <button class="warning-btn" @click="goToSecurity">{{ $t("tradePwd.goEnable") }}</button>
    </div>

    <div class="trade-pwd-form">
      <div class="form-row">
        <label class="form-label">{{ $t("tradePwd.newPwd") }}</label>
        <div class="form-control-wrap">
          <input
            type="password"
            v-model="form.new"
            :placeholder="$t('tradePwd.newPwdPlaceholder')"
            class="form-input"
            :disabled="isTwoFactor != 2"
          />
        </div>
      </div>
      <div class="form-row">
        <label class="form-label">{{ $t("tradePwd.confirmPwd") }}</label>
        <div class="form-control-wrap">
          <input
            type="password"
            v-model="form.confirm"
            :placeholder="$t('tradePwd.confirmPwdPlaceholder')"
            class="form-input"
            :disabled="isTwoFactor != 2"
          />
        </div>
      </div>
      <!-- 邮箱验证码 -->
      <div class="form-row">
        <label class="form-label">{{ $t("tradePwd.emailCode") }}</label>
        <div class="form-control-wrap">
          <input
            type="text"
            v-model="form.emailCode"
            :placeholder="$t('tradePwd.emailCodePlaceholder')"
            class="form-input"
            maxlength="6"
            :disabled="isTwoFactor != 2"
          />
          <button
            class="btn-captcha"
            @click="openTwoFactorModal"
            :disabled="codeCountdown > 0 || isTwoFactor != 2"
          >
            {{ codeCountdown > 0 ? $t("tradePwd.resend", { count: codeCountdown }) : $t("tradePwd.sendCode") }}
          </button>
        </div>
      </div>
      <div class="form-actions">
        <button class="btn-submit" @click="handleSave" :disabled="isTwoFactor != 2">
          {{ $t("tradePwd.confirmBtn") }}
        </button>
      </div>
    </div>

    <!-- ===== 两步验证弹窗 ===== -->
    <TwoFactorModal
      ref="twoFactorModal"
      :desc="$t('tradePwd.twofaDesc')"
      @confirm="onTwoFactorConfirm"
    />
  </div>
</template>

<script>
import TwoFactorModal from "@/components/TwoFactorModal.vue";

export default {
  name: "TradePwdPanel",
  components: {
    TwoFactorModal,
  },
  props: {
    isTwoFactor: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      form: {
        new: "",
        confirm: "",
        emailCode: "",
      },
      // 验证码倒计时
      codeCountdown: 0,
      codeTimer: null,
    };
  },
  beforeDestroy() {
    if (this.codeTimer) {
      clearInterval(this.codeTimer);
    }
  },
  methods: {
    // ============================================================
    // 0. 去开启两步验证
    // ============================================================
    goToSecurity() {
      this.$emit("go-security");
    },

    // ============================================================
    // 1. 点击「发送验证码」→ 打开两步验证弹窗
    // ============================================================
    openTwoFactorModal() {
      // 校验新交易密码
      if (this.form.new.length < 6) {
        this.$message.warning(this.$t("tradePwd.pwdTooShort"));
        return;
      }
      if (this.form.new !== this.form.confirm) {
        this.$message.warning(this.$t("tradePwd.pwdMismatch"));
        return;
      }

      // 检查是否开启两步验证
      if (this.isTwoFactor != 2) {
        this.$message.warning(this.$t("tradePwd.enableTwofaFirst"));
        return;
      }

      // 打开两步验证弹窗
      this.$refs.twoFactorModal.open();
    },

    // ============================================================
    // 2. 两步验证通过 → 发送邮箱验证码
    // ============================================================
    onTwoFactorConfirm(twoCode) {
      // 设置按钮为验证中状态
      this.$refs.twoFactorModal.setVerifying(true);

      // 调用发送邮箱验证码接口
      this.$api.code
        .sendSafetyCode({
          scene: "update_trade_pwd",
          twoCode,
        })
        .then((res) => {
          if (res.code == 1) {
            // 验证失败，显示错误并重置
            this.$refs.twoFactorModal.setError(res.msg || this.$t("tradePwd.codeError"));
            return;
          }
          this.$message.success(res.msg);
          this.$refs.twoFactorModal.close();
          this.startCountdown();
        });
    },

    // ============================================================
    // 3. 开始倒计时
    // ============================================================
    startCountdown() {
      this.codeCountdown = 60;
      this.codeTimer = setInterval(() => {
        this.codeCountdown--;
        if (this.codeCountdown <= 0) {
          clearInterval(this.codeTimer);
          this.codeTimer = null;
        }
      }, 1000);
    },

    // ============================================================
    // 4. 确认修改交易密码
    // ============================================================
    handleSave() {
      if (this.form.new.length < 6) {
        this.$message.warning(this.$t("tradePwd.pwdTooShort"));
        return;
      }
      if (this.form.new !== this.form.confirm) {
        this.$message.warning(this.$t("tradePwd.pwdMismatch"));
        return;
      }
      if (!this.form.emailCode) {
        this.$message.warning(this.$t("tradePwd.enterEmailCode"));
        return;
      }

      // 调用修改交易密码接口
      this.$api.user
        .updateTradePwd({
          new_passwd: this.form.new,
          confirm_passwd: this.form.confirm,
          code: this.form.emailCode,
        })
        .then((res) => {
          if (res.code === 1) {
            this.$message.error(res.msg);
            return;
          }
          this.$message.success(this.$t("tradePwd.success"));
          this.form.new = "";
          this.form.confirm = "";
          this.form.emailCode = "";
        });
    },
  },
};
</script>

<style scoped>
.content-panel {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.panel-title-wrap {
  margin-bottom: 28px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.panel-title {
  font-size: 20px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 4px 0;
}

.panel-sub {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.35);
}

/* ===== 未开启两步验证警告 ===== */
.twofa-warning {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  background: rgba(255, 77, 79, 0.08);
  border: 1px solid rgba(255, 77, 79, 0.2);
  border-radius: 10px;
  margin-bottom: 20px;
}

.warning-icon {
  font-size: 20px;
  flex-shrink: 0;
}

.warning-text {
  flex: 1;
  font-size: 14px;
  color: rgba(255, 107, 107, 0.9);
  line-height: 1.5;
}

.warning-btn {
  padding: 6px 20px;
  border-radius: 8px;
  background: rgba(255, 77, 79, 0.15);
  border: 1px solid rgba(255, 77, 79, 0.3);
  color: #ff6b6b;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
  white-space: nowrap;
}

.warning-btn:hover {
  background: rgba(255, 77, 79, 0.25);
  color: #fff;
}

/* ===== 表单行 ===== */
.form-row {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.form-label {
  width: 100px;
  flex-shrink: 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
}

.form-control-wrap {
  flex: 1;
  display: flex;
  gap: 12px;
  align-items: center;
}

.form-input {
  flex: 1;
  padding: 10px 16px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  outline: none;
  transition: all 0.3s ease;
  font-family: inherit;
  max-width: 400px;
}

.form-input::placeholder {
  color: rgba(255, 255, 255, 0.25);
}

.form-input:focus {
  border-color: rgba(0, 180, 255, 0.4);
  background: rgba(255, 255, 255, 0.08);
  box-shadow: 0 0 0 3px rgba(0, 180, 255, 0.05);
}

/* ===== 发送验证码按钮 ===== */
.btn-captcha {
  padding: 10px 20px;
  border-radius: 8px;
  background: rgba(0, 180, 255, 0.06);
  border: 1px solid rgba(0, 180, 255, 0.08);
  color: #00b4ff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.25s ease;
  font-family: inherit;
  white-space: nowrap;
}

.btn-captcha:hover:not(:disabled) {
  background: rgba(0, 180, 255, 0.12);
}

.btn-captcha:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ===== 底部提交按钮 ===== */
.form-actions {
  padding-top: 24px;
  display: flex;
  justify-content: flex-start;
}

.btn-submit {
  padding: 12px 48px;
  border-radius: 10px;
  background: linear-gradient(135deg, #00b4ff, #0066ff);
  border: none;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
}

.btn-submit:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(0, 100, 255, 0.3);
}

/* ===== 响应式 ===== */
@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 16px 0;
  }
  .form-label {
    width: 100%;
  }
  .form-control-wrap {
    width: 100%;
    flex-wrap: wrap;
  }
  .form-input {
    max-width: 100%;
    width: 100%;
  }
}

@media (max-width: 480px) {
  .panel-title {
    font-size: 17px;
  }
  .form-actions {
    width: 100%;
  }
  .btn-submit {
    width: 100%;
    justify-content: center;
    text-align: center;
  }
}
</style>
